import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FolderInput,
  Folder,
  Users,
  Lock,
  Rocket,
  Video,
  Youtube,
  Mic2,
  Music2,
  Radio,
  Headphones,
  Speaker,
  Podcast,
  Code,
  DollarSign,
  BarChart,
  Settings,
  PhoneCall,
  Layout,
  FileText,
  CalendarDays,
  GraduationCap,
  Briefcase,
  Building2,
  Lightbulb,
  Microscope,
  Activity,
  Plane,
  Film,
  Home,
  Utensils,
  Laptop,
  Trophy,
  ShoppingCart,
  Target,
  UsersRound,
  Scale,
  ClipboardList,
  BarChart3,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { RootState } from '../../../../reducers';
import { getTeamFolders, moveFile } from '../../selectedVideoSlice';
import useLimits from '../../../plan/hooks/limits';
import { usePlan } from '../../../plan/hooks/plan';
import useUserTags from '../../../../hooks/useTags';

const ICON_MAP = {
  Folder,
  Video,
  Youtube,
  Mic: Mic2,
  Music: Music2,
  Radio,
  Headphones,
  Speaker,
  Podcast,
  Code,
  DollarSign,
  BarChart,
  Settings,
  PhoneCall,
  Layout,
  FileText,
  CalendarDays,
  GraduationCap,
  Briefcase,
  Building2,
  Users,
  Lightbulb,
  Microscope,
  Activity,
  Plane,
  Film,
  Home,
  Utensils,
  Laptop,
  Trophy,
  ShoppingCart,
  Target,
  UsersRound,
  Scale,
  ClipboardList,
  BarChart3,
};

const MoveFileModal = ({ onClose }: { onClose: () => void }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const guardLimits = useLimits();
  const { isPlan } = usePlan();
  const { getTag } = useUserTags();
  const { team, selectedVideo } = useSelector((state: RootState) => ({
    team: state.team,
    selectedVideo: state.selectedVideo,
  }));
  const { selectedTeam, teams } = team;
  const { actions } = selectedVideo;
  const { globalModals } = useSelector((state: RootState) => state);
  const { files } = globalModals.actionData || {};
  const { folders } = actions.move;

  const folderIconsTag = getTag('folder_icons');
  const folderIcons = folderIconsTag ? JSON.parse(folderIconsTag) : {};

  const [selectedTeamId, setSelectedTeamId] = useState<string>(
    selectedTeam?.teamId || '',
  );
  const [selectedFolderId, setSelectedFolderId] = useState<string>('PRIVATE');

  const noFolders = folders.length === 0;
  const disableMoveButton = !selectedTeamId || noFolders;

  useEffect(() => {
    if (selectedTeamId) {
      dispatch(getTeamFolders(selectedTeamId));
    }
  }, [selectedTeamId]);

  const handleTeamChange = (teamId: string) => {
    setSelectedFolderId('PRIVATE');
    setSelectedTeamId(teamId);
    dispatch(getTeamFolders(teamId));
  };

  const handleClose = () => {
    if (onClose) onClose();
  };

  const handleUpgradeClick = () => {
    guardLimits('folderManagement', () => {
      // Handle upgrade logic here, if needed.
    });
  };

  const handleMove = async () => {
    if (!files?.length || disableMoveButton) return;
    await Promise.all(
      files.map((file) =>
        dispatch(
          moveFile({
            fileId: file._id,
            toTeamId: selectedTeamId,
            toFolderId:
              selectedFolderId === 'PRIVATE' ? 'private' : selectedFolderId,
          }),
        ),
      ),
    );
    navigate(
      `/library/${selectedTeamId}/${
        selectedFolderId === 'PRIVATE' ? 'private' : selectedFolderId
      }`,
    );
    handleClose();
  };

  const getFolderIcon = (folderId: string) => {
    const iconName = folderIcons[folderId] || 'Folder';
    const IconComponent = ICON_MAP[iconName as keyof typeof ICON_MAP] || Folder;
    return IconComponent;
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('selectedVideo.actions.modals.moveFile.title')}
          </DialogTitle>
          <DialogDescription>
            {t('selectedVideo.actions.modals.moveFile.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="team-select">
              {t('selectedVideo.actions.modals.moveFile.selectTeam')}
            </Label>
            <Select
              value={selectedTeamId}
              onValueChange={handleTeamChange}
              data-testid="team-select"
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {teams.map(({ teamId, name }) => (
                  <SelectItem key={teamId} value={teamId}>
                    <div className="flex items-center gap-2">
                      <Users className="h-4 w-4" />
                      {name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="folder-select">
              {t('selectedVideo.actions.modals.moveFile.selectFolder')}
            </Label>
            <Select
              value={selectedFolderId}
              onValueChange={setSelectedFolderId}
              data-testid="folder-select"
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="PRIVATE">
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4" />
                    {t('selectedVideo.actions.modals.moveFile.privateFolder')}
                  </div>
                </SelectItem>
                {folders.map((folder) => {
                  const FolderIcon = getFolderIcon(folder._id);
                  return (
                    <SelectItem key={folder._id} value={folder._id}>
                      <div className="flex items-center gap-2">
                        <FolderIcon className="h-4 w-4" />
                        {folder.name}
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>

          {!guardLimits('folderManagement') && !isPlan(['ENTERPRISE']) && (
            <Alert>
              <Rocket className="h-4 w-4" />
              <AlertDescription>
                {t(
                  'selectedVideo.actions.modals.moveFile.upgradeToCreateFolders',
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUpgradeClick}
                  className="ml-2"
                >
                  {t('selectedVideo.actions.modals.moveFile.upgradeNow')}
                </Button>
              </AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            data-testid="cancel-button-move-modal"
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            onClick={handleMove}
            disabled={disableMoveButton}
            data-testid="move-button-move-modal"
          >
            <FolderInput className="mr-2 h-4 w-4" />
            {t('selectedVideo.actions.modals.moveFile.move')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MoveFileModal;
